import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, User } from 'lucide-react';
import { BrandMark } from './BrandMark';
import { Footer } from './Footer';

/**
 * Shared page shell for legal/informational pages (About, Terms, Policies).
 * Keeps the sticky Warm-Trust header + Sign-In CTA consistent, then renders
 * `children` inside a readable prose column.
 */
export const LegalPage = ({ eyebrow, title, updated, children, testId }) => {
  return (
    <div className="theme-sunset" style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
      <header className="sticky top-0 z-30 backdrop-blur-md bg-[#E8DCC9]/85 border-b border-[#1A1A1A]/10">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <BrandMark size={36} textClass="text-base sm:text-lg text-[#1A1A1A]" />
          </Link>
          <nav className="hidden sm:flex items-center gap-6 text-xs uppercase tracking-[0.25em] text-[#1A1A1A]/70">
            <Link to="/" className="hover:text-[#FF5A1F] transition-colors">Home</Link>
            <Link to="/pricing" className="hover:text-[#FF5A1F] transition-colors">Pricing</Link>
            <Link to="/faq" className="hover:text-[#FF5A1F] transition-colors">FAQ</Link>
            <Link
              to="/?signin=1"
              className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-[#1A1A1A] text-white hover:bg-[#FF5A1F] transition-colors text-xs font-bold tracking-wide no-underline"
            >
              <User size={13} /> Sign In
            </Link>
          </nav>
        </div>
      </header>

      <section className="max-w-3xl mx-auto px-6 sm:px-8 py-10 sm:py-14" data-testid={testId}>
        <Link to="/" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[#525252] hover:text-[#FF5A1F] mb-6 transition-colors">
          <ArrowLeft size={14} /> Back to home
        </Link>

        {eyebrow && (
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#FF5A1F] font-bold mb-3">
            {eyebrow}
          </div>
        )}
        <h1
          className="text-4xl sm:text-5xl font-black uppercase mb-4 text-[#1A1A1A]"
          style={{ fontFamily: 'Cabinet Grotesk', lineHeight: '1' }}
        >
          {title}
        </h1>
        {updated && (
          <p className="text-xs text-[#737373] font-mono uppercase tracking-wide mb-10">
            Last updated: {updated}
          </p>
        )}

        <article className="prose-legal">
          {children}
        </article>
      </section>

      <Footer />

      {/* Scoped typography for the article body */}
      <style>{`
        .prose-legal h2 {
          font-family: 'Cabinet Grotesk', 'Inter', sans-serif;
          font-size: 1.35rem;
          font-weight: 800;
          text-transform: uppercase;
          letter-spacing: 0.02em;
          color: #1A1A1A;
          margin-top: 2.25rem;
          margin-bottom: 0.75rem;
        }
        .prose-legal h3 {
          font-family: 'Cabinet Grotesk', 'Inter', sans-serif;
          font-size: 1.05rem;
          font-weight: 700;
          color: #1A1A1A;
          margin-top: 1.5rem;
          margin-bottom: 0.4rem;
        }
        .prose-legal p, .prose-legal li {
          font-size: 0.95rem;
          line-height: 1.7;
          color: #333333;
        }
        .prose-legal p { margin-bottom: 1rem; }
        .prose-legal ul {
          list-style: disc;
          padding-left: 1.35rem;
          margin-bottom: 1rem;
        }
        .prose-legal ul li { margin-bottom: 0.35rem; }
        .prose-legal strong { color: #1A1A1A; }
        .prose-legal a { color: #FF5A1F; text-decoration: underline; text-underline-offset: 3px; }
      `}</style>
    </div>
  );
};

export default LegalPage;
